import {
  type CreateAdminUserDto,
  type UpdateAdminUserDto,
  type V2AdminUserDto,
  type V2AdminUsersPageDto,
  type V2AdminUsersSummaryDto,
  v2AdminUsersControllerAdvisors,
  v2AdminUsersControllerCreate,
  v2AdminUsersControllerDetail,
  v2AdminUsersControllerList,
  v2AdminUsersControllerLookup,
  v2AdminUsersControllerRemove,
  v2AdminUsersControllerResetPassword,
  v2AdminUsersControllerSummary,
  v2AdminUsersControllerUpdate
} from '@idc-forms/api-client'
import {
  V2AdminUserLookupSchema,
  V2AdminUserSchema,
  V2AdminUsersPageSchema,
  V2AdminUsersSummarySchema
} from '@idc-forms/schemas/api-v2/admin-users'
import { assertShape } from '@/shared/api/assert-shape'
import { bearer, v2RequestOptions } from '@/shared/api/v2-request'

/**
 * Administración de usuarios contra API v2 (ADR 0019/0020). Consume el SDK generado
 * con el Bearer de la sesión staff v2 (`auth.v2AccessToken`), pagina por cursor y usa
 * el contrato de consulta rico (filtros con operadores + lógicos, orden multi-campo,
 * búsqueda). Sustituye a `users-admin.service.ts` (v1, offset) detrás del feature flag.
 */

/** Parámetros amigables del listado; el servicio los mapea al query del SDK. */
export type UsersAdminV2Query = {
  /** Filtros ricos ya en forma `{ campo: { $op: valor }, $or: [...] }`. */
  filters?: Record<string, unknown>
  /** Orden `campo:asc|desc`. El endpoint v2 acepta un solo campo (desempata por id). */
  sort?: string
  search?: string
  pageSize?: number
  /** Cursor de avance; excluyente con `before`. */
  after?: string
  /** Cursor de retroceso; excluyente con `after`. */
  before?: string
}

export type UsersAdminV2Row = V2AdminUsersPageDto['data'][number]

/** Referencia de usuario para selects/matrices v2: id público (`usr_`) + lo mínimo. */
export type UserV2LookupOption = {
  id: string
  fullName: string
  email: string
  accountType: V2AdminUsersPageDto['data'][number]['accountType']
}

/**
 * Opción mínima del selector de asignación (`usr_` + nombre): la que consume el
 * endpoint dedicado `GET /admin/users/lookup` para elegir personas —los
 * autorizadores de un proyecto—, sin arrastrar email ni tipo de cuenta.
 */
export type UserV2SelectionOption = { id: string; fullName: string }

function toListQuery(query: UsersAdminV2Query) {
  return {
    ...(query.filters && Object.keys(query.filters).length ? { filters: query.filters } : {}),
    ...(query.sort ? { sort: query.sort } : {}),
    ...(query.search ? { search: query.search } : {}),
    ...(query.pageSize ? { 'page[size]': query.pageSize } : {}),
    ...(query.after ? { 'page[after]': query.after } : {}),
    ...(query.before ? { 'page[before]': query.before } : {})
  }
}

export const usersAdminV2Service = {
  /**
   * Usuarios como referencias `{ id: usr_, … }` para el editor de roles v2. Una
   * sola página amplia alcanza para el dataset actual; si la plantilla creciera
   * por encima del tope habría que paginar el selector.
   */
  lookup: async (accessToken: string): Promise<UserV2LookupOption[]> => {
    const result = await v2AdminUsersControllerList({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      query: { 'page[size]': 200, sort: 'fullName:asc' }
    })
    return assertShape(V2AdminUsersPageSchema, result.data).data.map((user) => ({
      id: user.id,
      fullName: user.fullName,
      email: user.email,
      accountType: user.accountType
    }))
  },

  /**
   * Asesores ACTIVOS para el selector de la ficha del cliente y de la negociación,
   * contra el endpoint dedicado `GET /admin/users/advisors`.
   *
   * Antes esto pedía el LISTADO de usuarios con `filters[accountType][$eq]=advisor`.
   * Funcionaba con un administrador y fallaba con todos los demás: el listado exige
   * `ReadUsers`, permiso que ningún rol operativo tiene, así que el selector le
   * respondía 403 a quien más lo usa. Además traía email, rol y estado de cuenta
   * para llenar un `<select>` de dos campos. El portón del endpoint dedicado es
   * `LOOKUP_ACCESS.advisors`: lo abre cualquier permiso de negociaciones o clientes,
   * que es donde vive el selector — la misma regla que `GET /users/advisor` en v1.
   */
  advisors: async (accessToken: string): Promise<UserV2SelectionOption[]> => {
    const result = await v2AdminUsersControllerAdvisors({
      ...v2RequestOptions,
      headers: bearer(accessToken)
    })
    return assertShape(V2AdminUserLookupSchema, result.data).data.map((user) => ({
      id: user.id,
      fullName: user.fullName
    }))
  },

  /**
   * Selector de asignación de personas contra el endpoint dedicado
   * `GET /admin/users/lookup` (`usr_` + nombre, sin paginar). Lo consume el
   * multi-select de autorizadores de un proyecto.
   */
  selectionLookup: async (accessToken: string): Promise<UserV2SelectionOption[]> => {
    const result = await v2AdminUsersControllerLookup({
      ...v2RequestOptions,
      headers: bearer(accessToken)
    })
    return assertShape(V2AdminUserLookupSchema, result.data).data.map((user) => ({
      id: user.id,
      fullName: user.fullName
    }))
  },

  list: async (accessToken: string, query: UsersAdminV2Query): Promise<V2AdminUsersPageDto> => {
    const result = await v2AdminUsersControllerList({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      query: toListQuery(query)
    })
    return assertShape(V2AdminUsersPageSchema, result.data)
  },

  summary: async (
    accessToken: string,
    query: Pick<UsersAdminV2Query, 'filters' | 'search'>
  ): Promise<V2AdminUsersSummaryDto> => {
    const result = await v2AdminUsersControllerSummary({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      query: toListQuery(query)
    })
    return assertShape(V2AdminUsersSummarySchema, result.data)
  },

  detail: async (accessToken: string, id: string): Promise<V2AdminUserDto> => {
    const result = await v2AdminUsersControllerDetail({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id }
    })
    return assertShape(V2AdminUserSchema, result.data)
  },

  create: async (accessToken: string, body: CreateAdminUserDto): Promise<V2AdminUserDto> => {
    const result = await v2AdminUsersControllerCreate({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      body
    })
    return result.data
  },

  update: async (
    accessToken: string,
    id: string,
    body: UpdateAdminUserDto
  ): Promise<V2AdminUserDto> => {
    const result = await v2AdminUsersControllerUpdate({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id },
      body
    })
    return result.data
  },

  remove: async (accessToken: string, id: string): Promise<void> => {
    await v2AdminUsersControllerRemove({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id }
    })
  },

  resetPassword: async (accessToken: string, id: string): Promise<void> => {
    await v2AdminUsersControllerResetPassword({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id }
    })
  }
}
